// NewSetPage: a new set from existing blueprints or a sample packet (blueprint-sets Req 1.2, 2.8, 8.1).
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Tabs from '@cloudscape-design/components/tabs';
import { api } from '../../api/client';
import { PacketUpload } from './PacketUpload';
import { SetMembersEditor } from './SetMembersEditor';
import { errorMessage, useNotify } from '../../lib/notify';
import { CoverageFloorInput } from './coverageFloor';
import { DetailsFields } from './setShared';
import { clearSetDraft, emptyState, fieldErrorsOf, useSetForm } from './setForm';

// ---------- NewSetPage (Req 1.2, 2.8, 8.1) ----------

export function NewSetPage() {
  const [tab, setTab] = useState('existing');
  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Pick blueprints you already have, or let the Studio propose a set from a sample packet."
      >
        Create blueprint set
      </Header>
      <Tabs
        activeTabId={tab}
        onChange={(e) => setTab(e.detail.activeTabId)}
        tabs={[
          { id: 'existing', label: 'Choose existing blueprints', content: <NewSetForm /> },
          { id: 'packet', label: 'Start from a sample packet', content: <PacketUpload /> },
        ]}
      />
    </SpaceBetween>
  );
}

function NewSetForm() {
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const [basis] = useState(emptyState);
  const f = useSetForm(undefined, basis, null);
  const [saving, setSaving] = useState(false);

  async function create() {
    f.setShowErrors(true);
    if (f.blocking || 'error' in f.floor) {
      notify.error('Fix the highlighted problems before saving.');
      return;
    }
    setSaving(true);
    try {
      const s = await api.createSet({
        name: f.state.name,
        description: f.state.description,
        members: f.state.members,
        summaryRules: f.state.summaryRules,
        coverageFloor: f.floor.share,
      });
      clearSetDraft(undefined);
      await qc.invalidateQueries({ queryKey: ['sets'] });
      notify.success(`Created "${s.name}".`);
      navigate(`/sets/${s.id}`);
    } catch (e) {
      const fe = fieldErrorsOf(e);
      if (fe) {
        f.applyApiErrors(fe);
        notify.error('Fix the highlighted problems before saving.');
      } else notify.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <SpaceBetween size="l">
      <DetailsFields
        name={f.state.name}
        description={f.state.description}
        onName={(v) => f.update('name', v)}
        onDescription={(v) => f.update('description', v)}
        nameError={f.nameError}
        descriptionError={f.descriptionError}
      />
      <SetMembersEditor
        value={f.state.members}
        onChange={(v) => f.update('members', v)}
        fieldErrors={f.memberApiErrors}
        onValidationChange={f.setMemberValidation}
      />
      <Container>
        <CoverageFloorInput
          value={f.state.floorText}
          onChange={(v) => f.update('floorText', v)}
          errorText={f.floorError}
        />
      </Container>
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => navigate('/sets')}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void create()}>
            Save
          </Button>
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  );
}
